import React, { FC, useRef, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import {
	Alert,
	Button,
	DatePicker,
	Form,
	Input,
	message,
	Modal,
	Radio,
	Select,
	Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import OWUpload from '../OWUpload';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import SelectWithAllToggle from '../common/SelectWithAllToggle';
import RegionsLocationsTreeSelect from '../regions_tree_select/RegionsLocationsTreeSelect';
import ProblemTypeTreeSelect from '../problem_type_tree_select';
import OWButton from '../ow_button/OWButton';
import ComplianceTypeFormModal from './ComplianceTypeFormModal';
import { ComplianceAiLoadingState } from '../compliance_common/ComplianceAiLoadingState';
import { ComplianceChecklistReview } from '../compliance_common/ComplianceChecklistReview';
import { complianceTypesRestCrudThunks } from '../../thunks/compliance_types_thunks';
import {
	complianceRequestsRestCrudThunks,
	publishComplianceRequest,
} from '../../thunks/compliance_requests_thunks';
import {
	extractComplianceChecklistFromSample,
	COMPLIANCE_SAMPLE_ACCEPTED_MIME_TYPES,
	COMPLIANCE_SAMPLE_MAX_FILE_SIZE_MB,
} from '../../thunks/compliance_checklists_thunks';
import { locationsRestCrudThunks } from '../../thunks/locations_thunks';
import {
	problemTypesRestCrudThunks,
	problemTypesHeirarchical,
} from '../../thunks/problem_types_thunks';
import {
	supplierFacilitiesRestCrudThunks,
	fetchSupplierFacilitiesInPrivateNetwork,
} from '../../thunks/supplier_facilities_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { withUserType } from '../withUserType';
import {
	COMPLIANCE_ASSIGNMENT_MODES,
	ComplianceRequest,
	ComplianceType,
} from '../../types/ComplianceTypes';
import { ChecklistExtraction } from '../../types/ComplianceChecklistTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getUploadedFileUrl } from '../../utils/FileAccessUtils';
import useConfirmClose from '../../hooks/useConfirmClose';

const TYPES_DROPDOWN_TARGET_COLLECTION = 'complianceTypesDropdown';
const LOCATIONS_TARGET_COLLECTION = 'COMPLIANCE_REQUEST_FORM_LOCATIONS_AUTOCOMPLETE';
const PROBLEM_TYPES_TARGET_COLLECTION = 'COMPLIANCE_REQUEST_FORM_PROBLEM_TYPES_AUTOCOMPLETE';
const SUPPLIERS_TARGET_COLLECTION = 'COMPLIANCE_REQUEST_FORM_SUPPLIER_FACILITIES_AUTOCOMPLETE';

// Create uses a stepped wizard to keep each screen light; the active step list is
// derived from the selected compliance type (SAMPLE is only present when the type
// allows a sample upload) and from whether a sample was actually uploaded
// (EXTRACTING + CHECKLIST). Edit shows every field on one screen. CREATED is the
// post-save confirmation (create only) and is not part of the input step list.
const STEPS = {
	REQUIREMENT: 'requirement',
	UNIQUE: 'unique',
	SAMPLE: 'sample',
	// Transient loading screen while the backend reads the uploaded sample.
	EXTRACTING: 'extracting',
	// Review of the requirements read out of the sample.
	CHECKLIST: 'checklist',
	SCOPE: 'scope',
	NAME: 'name',
	CREATED: 'created',
};

// Steps that replace the form rather than showing a slice of it. The form stays
// mounted (hidden) on these so field values survive; CREATED unmounts it.
const NON_FORM_STEPS = [STEPS.EXTRACTING, STEPS.CHECKLIST, STEPS.CREATED];

// The checklist review lists every requirement read from the sample, so it needs
// a good deal more room than the single-field wizard steps.
const WIZARD_MODAL_WIDTH = 587;
const CHECKLIST_MODAL_WIDTH = 900;

// Modal title per step (create only).
const STEP_MODAL_TITLES = {
	[STEPS.REQUIREMENT]: 'New Compliance Request',
	[STEPS.UNIQUE]: 'New Compliance Request: Unique compliance document',
	[STEPS.SAMPLE]: 'New Compliance Request: Sample document',
	[STEPS.EXTRACTING]: 'New Compliance Request: Reading your sample',
	[STEPS.CHECKLIST]: 'New Compliance Request: Requirements from your sample',
	[STEPS.SCOPE]: 'New Compliance Request for Suppliers',
	[STEPS.NAME]: 'Name this Request',
	[STEPS.CREATED]: 'Compliance Request Created',
};

interface NewComplianceRequestModalProps {
	visible: boolean;
	// Which compliance endpoints to hit. Falls back to the logged-in user's role
	// (session.userType, injected by withUserType) when not explicitly provided.
	roleType?: string;
	editingRequest?: ComplianceRequest;
	onCancel: () => void;
	onSuccess: () => void;
	// Redux slices passed to the async selects / tree-selects as `stateSlice`.
	complianceTypesSlice: any;
	problemTypesSlice: any;
	supplierFacilitiesSlice: any;
	locationsSlice: any;
	// Selected compliance types (dropdown collection) for step-derivation, sample
	// lookups, and name prefill.
	complianceTypes: ComplianceType[];
	saving: boolean;
	createComplianceRequest: (entity: any) => Promise<any>;
	updateComplianceRequest: (entity: any) => Promise<any>;
	publishRequest: (id: number) => Promise<any>;
	extractChecklist: (sampleFile: File) => Promise<ChecklistExtraction>;
	fetchComplianceTypes: (...args: any[]) => any;
	fetchMultipleComplianceTypes: (...args: any[]) => any;
	fetchMultipleLocations: (...args: any[]) => any;
	fetchProblemTypes: (...args: any[]) => any;
	fetchMultipleProblemTypes: (...args: any[]) => any;
	fetchSupplierFacilities: (...args: any[]) => any;
	fetchMultipleSupplierFacilities: (...args: any[]) => any;
}

const NewComplianceRequestModal: FC<NewComplianceRequestModalProps> = ({
	visible,
	roleType,
	userType,
	editingRequest,
	onCancel,
	onSuccess,
	complianceTypesSlice,
	problemTypesSlice,
	supplierFacilitiesSlice,
	locationsSlice,
	complianceTypes,
	saving,
	createComplianceRequest,
	updateComplianceRequest,
	publishRequest,
	extractChecklist,
	fetchComplianceTypes,
	fetchMultipleComplianceTypes,
	fetchMultipleLocations,
	fetchProblemTypes,
	fetchMultipleProblemTypes,
	fetchSupplierFacilities,
	fetchMultipleSupplierFacilities,
}): React.ReactElement => {
	const role = roleType || userType;
	const [form] = Form.useForm();
	const [step, setStep] = useState(STEPS.REQUIREMENT);
	const [sampleFileList, setSampleFileList] = useState([]);
	const [createdRequest, setCreatedRequest] = useState(null);
	// Whether the user has entered anything, so the discard-confirmation on close
	// only fires when there's real progress to lose.
	const [formDirty, setFormDirty] = useState(false);
	// Requirements read out of the uploaded sample, the uid of the staged file they
	// came from (so re-entering the step without changing the file doesn't
	// re-extract), and the failure message from the last attempt.
	const [checklistExtraction, setChecklistExtraction] = useState<ChecklistExtraction>(null);
	const [extractedSampleUid, setExtractedSampleUid] = useState<string>(null);
	const [extractionError, setExtractionError] = useState<string>(null);
	// Set when the buyer chooses to carry on after a failed extraction, so NEXT
	// stops retrying and the checklist steps drop out of the wizard.
	const [extractionSkipped, setExtractionSkipped] = useState(false);
	// Bumped whenever an extraction is superseded or cancelled, so a response that
	// lands late is ignored instead of yanking the user onto the review screen.
	const extractionRequestId = useRef(0);
	// Inline "+ Create new compliance type" flow from the type OWAsyncSelect.
	const [typeCreateName, setTypeCreateName] = useState('');
	const [typeCreateOpen, setTypeCreateOpen] = useState(false);
	// Resolves the OWAsyncSelect createNewEntity promise once the type modal
	// finishes (with the new type) or is cancelled (with null).
	const typeCreateResolve = useRef(null);
	const isEditing = !!editingRequest;

	// OWAsyncSelect calls this with the typed text; open the type-create modal and
	// resolve once it succeeds/cancels so the async-select selects the new type.
	const handleCreateComplianceType = (typedName) =>
		new Promise((resolve) => {
			typeCreateResolve.current = resolve;
			setTypeCreateName(typedName);
			setTypeCreateOpen(true);
		});

	const finishTypeCreate = (createdType) => {
		setTypeCreateOpen(false);
		setTypeCreateName('');
		if (createdType) {
			form.setFieldsValue({ complianceTypeId: nullSafeGet('id', createdType) });
		}
		if (typeCreateResolve.current) {
			typeCreateResolve.current(createdType || null);
			typeCreateResolve.current = null;
		}
	};

	// The selected compliance type record drives whether the SAMPLE step exists.
	const selectedComplianceTypeId = Form.useWatch('complianceTypeId', form);
	const selectedComplianceType = complianceTypes.find(
		(complianceType) => complianceType.id === selectedComplianceTypeId
	);
	const allowSample = !!nullSafeGet('allowSampleUpload', selectedComplianceType);

	// The most recent finished upload — the sample the checklist is read from.
	// OWUpload merges changes into a ref-held list, so replacing a sample can leave
	// the previous file in it; matching from the end keeps the stale entry from
	// winning. The extraction endpoint takes the file itself (multipart), so it
	// needs originFileObj; the S3 url is what gets persisted as sampleFileUrl.
	const uploadedSampleFile = [...sampleFileList].reverse().find((file) => file.status === 'done');
	const uploadedSampleUrl = uploadedSampleFile ? getUploadedFileUrl(uploadedSampleFile) : null;
	const uploadedSampleName = nullSafeGet('name', uploadedSampleFile);
	const uploadedSampleUid = nullSafeGet('uid', uploadedSampleFile);
	const uploadedSampleBlob = nullSafeGet('originFileObj', uploadedSampleFile);
	// NEXT has to wait for the upload to finish, otherwise there is no file for the
	// extraction to read and the checklist steps would be skipped silently.
	const isSampleUploading = sampleFileList.some((file) => file.status === 'uploading');
	// Extraction only happens on create, only when the type allows a sample, and
	// only once one is actually uploaded.
	const hasChecklistSteps = !isEditing && allowSample && !!uploadedSampleBlob && !extractionSkipped;
	// True when the extraction in state was produced from the sample currently
	// staged, i.e. NEXT can go straight to the review without re-extracting.
	const hasCurrentExtraction = !!checklistExtraction && extractedSampleUid === uploadedSampleUid;
	// Editing never re-extracts, so a replacement sample leaves the request holding
	// the previous document's requirements. Treat the stored checklist as void as
	// soon as the staged sample differs from the one it was read from.
	const sampleReplacedOnEdit =
		isEditing &&
		!!uploadedSampleUrl &&
		uploadedSampleUrl !== nullSafeGet('sampleFileUrl', editingRequest);

	// Active input steps, recomputed on render so choosing a type that allows a
	// sample upload inserts the SAMPLE step immediately. SAMPLE is index-based so
	// skipping it "just works" for navigation and the progress bar.
	const activeSteps = [STEPS.REQUIREMENT, STEPS.UNIQUE]
		.concat(allowSample ? [STEPS.SAMPLE] : [])
		.concat(hasChecklistSteps ? [STEPS.EXTRACTING, STEPS.CHECKLIST] : [])
		.concat([STEPS.SCOPE, STEPS.NAME]);

	// A field group is visible when editing (single form) or on its own wizard step.
	const groupVisible = (groupStep) => isEditing || step === groupStep;
	const isFormStep = isEditing || NON_FORM_STEPS.indexOf(step) === -1;

	// Only validate the fields on the current step before advancing. dueBy exists
	// only when Mandatory; the assignment field depends on the chosen mode.
	const stepFieldNames = (currentStep) => {
		if (currentStep === STEPS.REQUIREMENT) {
			const names = ['complianceTypeId', 'isMandatory'];
			if (form.getFieldValue('isMandatory')) {
				names.push('dueBy');
			}
			return names;
		}
		if (currentStep === STEPS.UNIQUE) {
			return ['requiresUniqueDocument'];
		}
		if (currentStep === STEPS.SAMPLE) {
			return [];
		}
		if (currentStep === STEPS.SCOPE) {
			const names = ['locationIds'];
			names.push(
				form.getFieldValue('assignmentMode') === COMPLIANCE_ASSIGNMENT_MODES.PROBLEM_TYPES
					? 'problemTypeIds'
					: 'supplierFacilityIds'
			);
			return names;
		}
		if (currentStep === STEPS.NAME) {
			return ['name'];
		}
		return [];
	};

	// Best-effort resolve a display name from a target collection by id.
	// getRecordsForTargetCollection throws when the collection isn't present in the
	// slice yet, so guard for it — an unresolved name is simply omitted.
	const resolveName = (stateSlice, targetCollectionName, id) => {
		if (!id || !stateSlice || !stateSlice[targetCollectionName]) {
			return null;
		}
		const records = getRecordsForTargetCollection(stateSlice, targetCollectionName);
		const match = records.find((record) => record.id === id);
		return match ? nullSafeGet('name', match) : null;
	};

	// Prefill the request name when the user first reaches NAME (and hasn't typed
	// one). Specific location + supplier -> type_location_supplier; otherwise just
	// the compliance type name.
	const prefillName = () => {
		if (form.getFieldValue('name')) {
			return;
		}
		const complianceTypeName = nullSafeGetOrElse('name', selectedComplianceType, '');
		const allLocations = !!form.getFieldValue('allLocations');
		const assignmentMode = form.getFieldValue('assignmentMode');
		const locationIds = form.getFieldValue('locationIds');
		const supplierIds = form.getFieldValue('supplierFacilityIds');
		const allSuppliers = !!form.getFieldValue('allSuppliers');

		const isSupplierMode = assignmentMode === COMPLIANCE_ASSIGNMENT_MODES.SUPPLIERS;
		const singleSupplierId =
			isSupplierMode && !allSuppliers && Array.isArray(supplierIds) && supplierIds.length === 1
				? supplierIds[0]
				: null;
		// Only prefill a location name when exactly one location is selected.
		const singleLocationId =
			Array.isArray(locationIds) && locationIds.length === 1 ? locationIds[0] : null;

		const locationName = allLocations
			? null
			: resolveName(locationsSlice, LOCATIONS_TARGET_COLLECTION, singleLocationId);
		const supplierName = resolveName(
			supplierFacilitiesSlice,
			SUPPLIERS_TARGET_COLLECTION,
			singleSupplierId
		);

		const prefill =
			complianceTypeName && locationName && supplierName
				? `${complianceTypeName}_${locationName}_${supplierName}`
				: complianceTypeName;
		if (prefill) {
			form.setFieldsValue({ name: prefill });
		}
	};

	// Kick off the extraction and park on the loading step until it lands. Any
	// in-flight attempt is abandoned (see extractionRequestId).
	const startExtraction = () => {
		const requestId = extractionRequestId.current + 1;
		extractionRequestId.current = requestId;
		const sampleUid = uploadedSampleUid;
		setExtractionError(null);
		setChecklistExtraction(null);
		setExtractedSampleUid(null);
		setStep(STEPS.EXTRACTING);
		extractChecklist(uploadedSampleBlob).then(
			(extraction) => {
				if (extractionRequestId.current !== requestId) {
					return;
				}
				setChecklistExtraction(extraction);
				setExtractedSampleUid(sampleUid);
				setStep(STEPS.CHECKLIST);
			},
			(error) => {
				if (extractionRequestId.current !== requestId) {
					return;
				}
				setExtractionError(
					typeof error === 'string'
						? error
						: nullSafeGetOrElse('message', error, 'We could not read this document.')
				);
				setStep(STEPS.SAMPLE);
			}
		);
	};

	const goNext = () => {
		const currentIndex = activeSteps.indexOf(step);
		const nextStep = activeSteps[currentIndex + 1];
		form.validateFields(stepFieldNames(step)).then(
			() => {
				if (nextStep === STEPS.EXTRACTING) {
					// This exact sample has already been read — straight to the review.
					if (hasCurrentExtraction) {
						setStep(STEPS.CHECKLIST);
					} else {
						startExtraction();
					}
					return;
				}
				if (nextStep === STEPS.NAME) {
					prefillName();
				}
				setStep(nextStep);
			},
			() => {}
		);
	};

	const goBack = () => {
		// EXTRACTING is a transient loading screen, so stepping back off the review
		// lands on the sample upload rather than re-running the extraction.
		if (step === STEPS.CHECKLIST) {
			setStep(STEPS.SAMPLE);
			return;
		}
		const currentIndex = activeSteps.indexOf(step);
		if (currentIndex > 0) {
			setStep(activeSteps[currentIndex - 1]);
		}
	};

	// BACK while the sample is being read: abandon the in-flight extraction.
	const handleCancelExtraction = () => {
		extractionRequestId.current = extractionRequestId.current + 1;
		setStep(STEPS.SAMPLE);
	};

	// Escape hatch offered after a failed extraction so a flaky read can't trap the
	// buyer on the sample step. The sample still uploads with the request.
	const handleSkipExtraction = () => {
		extractionRequestId.current = extractionRequestId.current + 1;
		setExtractionSkipped(true);
		setExtractionError(null);
		setStep(STEPS.SCOPE);
	};

	const buildEntity = (values) => {
		return {
			name: values.name,
			countryCode: 'US',
			complianceTypeId: values.complianceTypeId,
			isMandatory: values.isMandatory,
			dueBy: values.isMandatory && values.dueBy ? values.dueBy.toISOString() : undefined,
			requiresUniqueDocument: values.requiresUniqueDocument,
			allLocations: !!values.allLocations,
			locationIds: values.allLocations ? [] : nullSafeGetOrElse('locationIds', values, []),
			assignmentMode: values.assignmentMode,
			allProblemTypes:
				values.assignmentMode === COMPLIANCE_ASSIGNMENT_MODES.PROBLEM_TYPES
					? !!values.allProblemTypes
					: false,
			problemTypeIds:
				values.assignmentMode === COMPLIANCE_ASSIGNMENT_MODES.PROBLEM_TYPES &&
				!values.allProblemTypes
					? nullSafeGetOrElse('problemTypeIds', values, [])
					: [],
			allSuppliers:
				values.assignmentMode === COMPLIANCE_ASSIGNMENT_MODES.SUPPLIERS
					? !!values.allSuppliers
					: false,
			supplierFacilityIds:
				values.assignmentMode === COMPLIANCE_ASSIGNMENT_MODES.SUPPLIERS && !values.allSuppliers
					? nullSafeGetOrElse('supplierFacilityIds', values, [])
					: [],
			notes: values.notes,
			// Only attach a sample when the selected compliance type allows one — a buyer
			// can upload a sample then switch to a type where allowSampleUpload is false,
			// which hides the SAMPLE step but leaves the staged file in state.
			sampleFileUrl: allowSample
				? uploadedSampleUrl || nullSafeGet('sampleFileUrl', editingRequest)
				: undefined,
			// The model's own JSON, reviewed by the buyer on the CHECKLIST step and
			// persisted opaquely by the backend (no server-side validation), so it goes
			// up exactly as it came back. An edit keeps whatever is already stored
			// (buildEntity's value overwrites the spread of editingRequest, so falling
			// back to it is what stops an edit from wiping the checklist) — unless the
			// sample was replaced, in which case it is dropped so the backend clears it
			// rather than pairing the new document with the old requirements.
			checklistExtraction:
				allowSample && !sampleReplacedOnEdit
					? hasCurrentExtraction
						? checklistExtraction
						: nullSafeGet('checklistExtraction', editingRequest)
					: undefined,
		};
	};

	// NAME's CREATE: build + create the request, then advance to CREATED. The
	// backend defaults new requests to Draft (and fans out Draft instances), so the
	// create body carries no status. Publishing happens only from the CREATED screen.
	const handleCreate = () => {
		form.validateFields().then(
			(values) => {
				createComplianceRequest(buildEntity(values)).then((savedRequest) => {
					setCreatedRequest(savedRequest);
					setStep(STEPS.CREATED);
				});
			},
			() => {}
		);
	};

	const resetState = () => {
		form.resetFields();
		setStep(STEPS.REQUIREMENT);
		setSampleFileList([]);
		setCreatedRequest(null);
		setFormDirty(false);
		// Abandon any in-flight extraction along with its result.
		extractionRequestId.current = extractionRequestId.current + 1;
		setChecklistExtraction(null);
		setExtractedSampleUid(null);
		setExtractionError(null);
		setExtractionSkipped(false);
	};

	// Edit saves the whole form in one shot and closes — no wizard, no publish step.
	const handleEditSave = () => {
		form.validateFields().then(
			(values) => {
				updateComplianceRequest({ ...editingRequest, ...buildEntity(values) }).then(() => {
					resetState();
					onSuccess();
				});
			},
			() => {}
		);
	};

	const handlePublishNow = () => {
		publishRequest(nullSafeGet('id', createdRequest)).then(() => {
			resetState();
			onSuccess();
		});
	};

	const handlePublishLater = () => {
		resetState();
		onSuccess();
	};

	const handleCancel = () => {
		resetState();
		onCancel();
	};

	// Mid-request = user has entered something, advanced past the first step, or
	// staged a sample. The CREATED step is already saved, so closing there just
	// finishes without a prompt.
	const isDirty =
		step !== STEPS.CREATED &&
		(formDirty || step !== STEPS.REQUIREMENT || sampleFileList.length > 0);
	const requestClose = useConfirmClose(isDirty, handleCancel);

	// A new file invalidates the previous attempt: clear the error and the skip so
	// the checklist steps come back for the file just staged.
	//
	// OWUpload merges changes into its own ref-held list instead of honouring
	// maxCount, so a replacement leaves the file it replaced behind. Keep only the
	// newest entry, and treat a removal as emptying the field outright — otherwise
	// removing a replacement would resurrect the sample it replaced.
	const handleSampleChange = (info) => {
		const isRemoval = nullSafeGet('file.status', info) === 'removed';
		const files = nullSafeGetOrElse('fileList', info, []);
		setSampleFileList(isRemoval ? [] : files.slice(-1));
		setExtractionError(null);
		setExtractionSkipped(false);
	};

	// The extraction endpoint reads a PDF and its body parser caps the upload, so
	// reject anything it could not handle up front rather than after two uploads.
	// Create only — editing keeps whatever sample is already attached.
	// LIST_IGNORE rather than false on rejection: `false` blocks the upload but
	// antd still leaves the intercepted file in the controlled fileList, where it
	// would sit looking like the selected sample with no status.
	const beforeSampleUpload = (file) => {
		// Some environments hand over a .pdf with no MIME metadata at all, so fall
		// back to the extension instead of rejecting a valid sample.
		const isPdf =
			COMPLIANCE_SAMPLE_ACCEPTED_MIME_TYPES.indexOf(file.type) !== -1 ||
			(!file.type && /\.pdf$/i.test(nullSafeGetOrElse('name', file, '')));
		if (!isPdf) {
			message.error('Please upload the sample as a PDF.');
			return Upload.LIST_IGNORE;
		}
		if (file.size / 1024 / 1024 >= COMPLIANCE_SAMPLE_MAX_FILE_SIZE_MB) {
			message.error(`The sample must be smaller than ${COMPLIANCE_SAMPLE_MAX_FILE_SIZE_MB}MB.`);
			return Upload.LIST_IGNORE;
		}
		return true;
	};

	const renderFormStep = () => {
		const initialDueBy = nullSafeGet('dueBy', editingRequest);
		return (
			<Form
				form={form}
				layout="vertical"
				preserve={false}
				onValuesChange={() => setFormDirty(true)}
				initialValues={{
					complianceTypeId: nullSafeGet('complianceTypeId', editingRequest),
					name: nullSafeGet('name', editingRequest),
					isMandatory: nullSafeGetOrElse('isMandatory', editingRequest, true),
					dueBy: initialDueBy ? moment(initialDueBy) : undefined,
					requiresUniqueDocument: nullSafeGetOrElse(
						'requiresUniqueDocument',
						editingRequest,
						false
					),
					allLocations: nullSafeGetOrElse('allLocations', editingRequest, true),
					locationIds: nullSafeGetOrElse('locationIds', editingRequest, []),
					assignmentMode: nullSafeGetOrElse(
						'assignmentMode',
						editingRequest,
						COMPLIANCE_ASSIGNMENT_MODES.PROBLEM_TYPES
					),
					problemTypeIds: nullSafeGetOrElse('problemTypeIds', editingRequest, []),
					allProblemTypes: nullSafeGetOrElse('allProblemTypes', editingRequest, false),
					allSuppliers: nullSafeGetOrElse('allSuppliers', editingRequest, false),
					supplierFacilityIds: nullSafeGetOrElse('supplierFacilityIds', editingRequest, []),
					notes: nullSafeGet('notes', editingRequest),
				}}
			>
				<div className={groupVisible(STEPS.REQUIREMENT) ? '' : 'hidden'}>
					<Form.Item
						name="complianceTypeId"
						label="Compliance type"
						rules={[{ required: true, message: 'Please select a compliance type.' }]}
					>
						<OWAsyncSelect
							placeholder="Select or type a compliance type"
							stateSlice={complianceTypesSlice}
							targetCollectionName={TYPES_DROPDOWN_TARGET_COLLECTION}
							fetchData={fetchComplianceTypes}
							fetchMultiple={fetchMultipleComplianceTypes}
							valueAccessor={(complianceType) => complianceType.id}
							renderRecord={(complianceType) => (
								<Select.Option key={complianceType.id} value={complianceType.id}>
									{complianceType.name}
								</Select.Option>
							)}
							newEntityLabelChecker={(complianceType) => complianceType.name}
							createNewEntity={handleCreateComplianceType}
							sortBy={{ sort_by: 'name', order: 'ascend' }}
							allowClear={true}
						/>
					</Form.Item>
					<Form.Item
						name="isMandatory"
						label="Mandatory or Optional?"
						// Hide the asterisk (the label already says it's a choice) but keep
						// the field required.
						required={false}
						rules={[{ required: true, message: 'Please choose an option.' }]}
					>
						<Radio.Group>
							<div className="flex flex-col gap-2">
								<Radio value={true}>
									Mandatory.{' '}
									<span className="text-character-secondary">
										Suppliers without this compliance type are non-compliant.
									</span>
								</Radio>
								<Radio value={false}>
									Optional.{' '}
									<span className="text-character-secondary">
										Suppliers without this compliance type remain compliant.
									</span>
								</Radio>
							</div>
						</Radio.Group>
					</Form.Item>
					<Form.Item noStyle={true} shouldUpdate={true}>
						{() =>
							form.getFieldValue('isMandatory') ? (
								<Form.Item
									name="dueBy"
									label="Due by:"
									className="ml-6"
									rules={[{ required: true, message: 'Please pick a due date.' }]}
									extra="Suppliers must submit and receive their approval by this date to remain compliant."
								>
									<DatePicker className="w-full" format="D MMM, YYYY" />
								</Form.Item>
							) : null
						}
					</Form.Item>
				</div>
				<div className={groupVisible(STEPS.UNIQUE) ? '' : 'hidden'}>
					<Form.Item
						name="requiresUniqueDocument"
						label="Requires a unique compliance document?"
						rules={[{ required: true, message: 'Please choose an option.' }]}
					>
						<Radio.Group>
							<div className="flex flex-col gap-2">
								<div className="footnoteregular mb-1 text-character-secondary">
									'Yes' if a general compliance document is insufficient and a unique document is
									required to fulfill this request
								</div>
								<Radio value={true}>Yes, requires unique document from supplier.</Radio>
								<Radio value={false}>No, general documents acceptable</Radio>
							</div>
						</Radio.Group>
					</Form.Item>
				</div>
				{/* SAMPLE group only renders when editing or when the type allows an upload. */}
				{isEditing || allowSample ? (
					<div className={groupVisible(STEPS.SAMPLE) ? '' : 'hidden'}>
						<div className="mb-4">
							<div className="mb-1">Sample</div>
							<OWUpload
								fileList={sampleFileList}
								onChange={handleSampleChange}
								maxCount={1}
								accept={isEditing ? undefined : '.pdf'}
								beforeUpload={isEditing ? undefined : beforeSampleUpload}
							>
								<Button icon={<UploadOutlined />}>Upload sample</Button>
							</OWUpload>
							{isEditing ? null : (
								<div className="footnoteregular mt-2 text-character-secondary">
									We read your sample and turn it into a checklist of requirements — limits, dates,
									checkboxes and named entities — for you to review on the next step.
								</div>
							)}
							{hasCurrentExtraction ? (
								<div className="footnoteregular mt-2 text-character-secondary">
									{nullSafeGetOrElse('items.length', checklistExtraction, 0)} requirements were read
									from this sample.
								</div>
							) : null}
							{extractionError ? (
								<Alert
									className="mt-3"
									type="warning"
									showIcon={true}
									message="We could not read this sample"
									description={`${extractionError} Try again, upload a clearer copy, or continue without a checklist.`}
								/>
							) : null}
						</div>
					</div>
				) : null}
				<div className={groupVisible(STEPS.SCOPE) ? '' : 'hidden'}>
					<SelectWithAllToggle
						label="Location"
						name="locationIds"
						allName="allLocations"
						form={form}
						displaySelectAllWithLabel={true}
						required={true}
						requiredIfNotAllSelected={true}
						selectAllPlaceholder="All locations"
					>
						<RegionsLocationsTreeSelect
							mode="multiple"
							targetCollectionName={LOCATIONS_TARGET_COLLECTION}
							fetchMultiple={fetchMultipleLocations}
						/>
					</SelectWithAllToggle>
					<Form.Item name="assignmentMode" label="Assign Suppliers by:">
						<Radio.Group buttonStyle="outline">
							<Radio.Button value={COMPLIANCE_ASSIGNMENT_MODES.PROBLEM_TYPES}>
								Problem Types
							</Radio.Button>
							<Radio.Button value={COMPLIANCE_ASSIGNMENT_MODES.SUPPLIERS}>Supplier</Radio.Button>
						</Radio.Group>
					</Form.Item>
					<Form.Item noStyle={true} shouldUpdate={true}>
						{() =>
							form.getFieldValue('assignmentMode') === COMPLIANCE_ASSIGNMENT_MODES.PROBLEM_TYPES ? (
								<SelectWithAllToggle
									label="Problem types"
									name="problemTypeIds"
									allName="allProblemTypes"
									form={form}
									displaySelectAllWithLabel={true}
									required={true}
									requiredIfNotAllSelected={true}
									selectAllPlaceholder="All problem types"
								>
									<ProblemTypeTreeSelect
										mode="multiple"
										collapseSelectionForDisplay={true}
										stateSlice={problemTypesSlice}
										targetCollectionName={PROBLEM_TYPES_TARGET_COLLECTION}
										additionalFilters={{}}
										fetchData={(
											searchText,
											targetCollectionName,
											pagination,
											sorting,
											filters,
											add
										) =>
											fetchProblemTypes(
												{ name: searchText },
												targetCollectionName,
												pagination,
												sorting,
												filters,
												add
											)
										}
										fetchMultiple={fetchMultipleProblemTypes}
										sortBy={{ sort_by: 'name', order: 'ascend' }}
									/>
								</SelectWithAllToggle>
							) : (
								<SelectWithAllToggle
									label="Suppliers"
									name="supplierFacilityIds"
									allName="allSuppliers"
									form={form}
									displaySelectAllWithLabel={true}
									required={true}
									requiredIfNotAllSelected={true}
									selectAllPlaceholder="All suppliers"
								>
									<OWAsyncSelect
										mode="multiple"
										placeholder="Select suppliers"
										stateSlice={supplierFacilitiesSlice}
										targetCollectionName={SUPPLIERS_TARGET_COLLECTION}
										fetchData={fetchSupplierFacilities}
										fetchMultiple={fetchMultipleSupplierFacilities}
										renderRecord={(supplierFacility) => (
											<Select.Option key={supplierFacility.id} value={supplierFacility.id}>
												{supplierFacility.name}
											</Select.Option>
										)}
										sortBy={{ sort_by: 'name', order: 'ascend' }}
									/>
								</SelectWithAllToggle>
							)
						}
					</Form.Item>
				</div>
				<div className={groupVisible(STEPS.NAME) ? '' : 'hidden'}>
					<Form.Item
						name="name"
						label="Request Name"
						rules={[{ required: true, message: 'Please name this request.' }]}
					>
						<Input />
					</Form.Item>
					<Form.Item name="notes" label="Additional Notes">
						<Input.TextArea rows={3} placeholder="additional notes about this request" />
					</Form.Item>
				</div>
			</Form>
		);
	};

	const renderCreatedStep = () => {
		const dueBy = nullSafeGet('dueBy', createdRequest);
		const isMandatory = nullSafeGet('isMandatory', createdRequest);
		return (
			<div className="flex flex-col gap-3 py-4">
				<div className="bodyregular text-character-secondary">
					Your compliance request is saved as a draft. To send it to your suppliers, you'll need to
					publish it.
				</div>
				{isMandatory && dueBy ? (
					<div className="bodyregular text-character-secondary">
						Once you publish, suppliers must submit and receive your approval by{' '}
						{moment(dueBy).format('D MMM, YYYY')} to remain compliant.
					</div>
				) : null}
			</div>
		);
	};

	const footerByStep = () => {
		// Edit: one screen, save-and-close (no wizard, no publish).
		if (isEditing) {
			return [
				<Button key="cancel" onClick={requestClose}>
					CANCEL
				</Button>,
				<OWButton key="save" type="primary" loading={saving} onClick={handleEditSave}>
					SAVE
				</OWButton>,
			];
		}
		if (step === STEPS.CREATED) {
			return [
				<Button key="later" onClick={handlePublishLater}>
					PUBLISH LATER
				</Button>,
				<OWButton key="now" type="primary" loading={saving} onClick={handlePublishNow}>
					PUBLISH NOW
				</OWButton>,
			];
		}
		// The sample is being read: only a way back out, no way forward yet.
		if (step === STEPS.EXTRACTING) {
			return [
				<Button key="back" onClick={handleCancelExtraction}>
					BACK
				</Button>,
				<OWButton key="next" type="primary" loading={true} disabled={true}>
					NEXT
				</OWButton>,
			];
		}
		// Wizard input steps.
		const backButton =
			step === STEPS.REQUIREMENT ? (
				<Button key="cancel" onClick={requestClose}>
					CANCEL
				</Button>
			) : (
				<Button key="back" onClick={goBack}>
					BACK
				</Button>
			);
		// Offered only after a failed read, so a flaky extraction can't dead-end the
		// wizard on the sample step.
		const skipButton =
			step === STEPS.SAMPLE && extractionError ? (
				<Button key="skip" onClick={handleSkipExtraction}>
					CONTINUE WITHOUT CHECKLIST
				</Button>
			) : null;
		const primaryButton =
			step === STEPS.NAME ? (
				<OWButton key="create" type="primary" loading={saving} onClick={handleCreate}>
					CREATE
				</OWButton>
			) : (
				// NEXT on the sample step fires the extraction call, so it carries the
				// OWButton double-click guard like the other network actions.
				<OWButton
					key="next"
					type="primary"
					loading={step === STEPS.SAMPLE && isSampleUploading}
					onClick={goNext}
				>
					{step === STEPS.SAMPLE && extractionError ? 'TRY AGAIN' : 'NEXT'}
				</OWButton>
			);
		return [backButton, skipButton, primaryButton].filter(Boolean);
	};

	const modalTitle = isEditing
		? 'Edit Compliance Request'
		: nullSafeGetOrElse(step, STEP_MODAL_TITLES, 'New Compliance Request');

	return (
		<>
			<Modal
				visible={visible}
				title={modalTitle}
				onCancel={requestClose}
				footer={footerByStep()}
				destroyOnClose={true}
				keyboard={false}
				maskClosable={false}
				width={step === STEPS.CHECKLIST ? CHECKLIST_MODAL_WIDTH : WIZARD_MODAL_WIDTH}
				// The review can run to dozens of requirements — scroll the list, not the page.
				bodyStyle={step === STEPS.CHECKLIST ? { maxHeight: '60vh', overflowY: 'auto' } : undefined}
			>
				{/* The form stays mounted (hidden) on the extraction steps so the values
				    entered so far survive; CREATED unmounts it. */}
				{step !== STEPS.CREATED ? (
					<div className={isFormStep ? '' : 'hidden'}>{renderFormStep()}</div>
				) : null}
				{step === STEPS.EXTRACTING ? (
					<ComplianceAiLoadingState
						title="Reading your sample document"
						description="We are pulling the requirements out of your sample, including anything you marked up on it. This usually takes under a minute."
						fileName={uploadedSampleName}
					/>
				) : null}
				{step === STEPS.CHECKLIST ? (
					<ComplianceChecklistReview
						extraction={checklistExtraction}
						fileName={uploadedSampleName}
					/>
				) : null}
				{step === STEPS.CREATED ? renderCreatedStep() : null}
			</Modal>
			{typeCreateOpen ? (
				<ComplianceTypeFormModal
					visible={typeCreateOpen}
					roleType={role}
					initialName={typeCreateName}
					onCancel={() => finishTypeCreate(null)}
					onSuccess={(createdType) => finishTypeCreate(createdType)}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	complianceTypesSlice: state.compliance_types,
	problemTypesSlice: state.problem_types,
	supplierFacilitiesSlice: state.supplier_facilities,
	locationsSlice: state.locations,
	complianceTypes: getRecordsForTargetCollection(
		state.compliance_types,
		TYPES_DROPDOWN_TARGET_COLLECTION
	),
	saving: state.compliance_requests.creating || state.compliance_requests.updating,
});

const mapDispatchToProps = (dispatch, ownProps) => {
	const role = ownProps.roleType || ownProps.userType;
	return {
		// OWAsyncSelect passes the search text as the first arg; wrap it into a
		// params object ({ name }) so the thunk doesn't spread the raw string into
		// per-character query params.
		fetchComplianceTypes: (searchText, targetCollectionName, pagination, sorting, filters, add) =>
			dispatch(
				complianceTypesRestCrudThunks(role).read(
					searchText ? { name: searchText } : {},
					targetCollectionName,
					pagination,
					sorting,
					filters,
					add
				)
			),
		fetchMultipleComplianceTypes: (ids, targetCollectionName) =>
			dispatch(complianceTypesRestCrudThunks(role).readMultiple(ids, targetCollectionName)),
		fetchMultipleLocations: (ids, targetCollectionName) =>
			dispatch(locationsRestCrudThunks(role).readMultiple(ids, targetCollectionName)),
		// Problem types are hierarchical — fetch the /with_children tree, not the flat list.
		fetchProblemTypes: (params, targetCollectionName, pagination, sorting, filters, add) =>
			dispatch(
				problemTypesHeirarchical(role)(
					params,
					targetCollectionName,
					pagination,
					sorting,
					filters,
					add
				)
			),
		fetchMultipleProblemTypes: (ids, targetCollectionName) =>
			dispatch(problemTypesRestCrudThunks(role).readMultiple(ids, targetCollectionName)),
		// Suppliers come from the caller's private network (same source as the Work
		// Order filters), not the raw supplier_facilities list. OWAsyncSelect passes
		// the search text as the first arg; wrap it into { name } so the thunk sends
		// a real search param instead of spreading the string char-by-char.
		fetchSupplierFacilities: (
			searchText,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			add
		) =>
			dispatch(
				fetchSupplierFacilitiesInPrivateNetwork(role)(
					searchText ? { name: searchText } : {},
					targetCollectionName,
					pagination,
					sorting,
					filters,
					add
				)
			),
		fetchMultipleSupplierFacilities: (ids, targetCollectionName) =>
			dispatch(supplierFacilitiesRestCrudThunks(role).readMultiple(ids, targetCollectionName)),
		createComplianceRequest: (entity) =>
			dispatch(complianceRequestsRestCrudThunks(role).create(entity)),
		updateComplianceRequest: (entity) =>
			dispatch(complianceRequestsRestCrudThunks(role).update(entity)),
		publishRequest: (id) => dispatch(publishComplianceRequest(role)(id)),
		extractChecklist: (sampleFile) =>
			dispatch(extractComplianceChecklistFromSample(role)(sampleFile)),
	};
};

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(NewComplianceRequestModal)
);
